import React, { useEffect, useState, useCallback } from 'react';
import {
  Container,
  Row,
  Col,
  Button,
  Modal,
  Card,
  Alert,
  OverlayTrigger,
  Tooltip,
  Spinner
} from 'react-bootstrap';
import { toast } from 'react-toastify';
import { debounce, isEmpty } from 'lodash';
import DynamicForm from 'screens/common/dynamicForm';
import TableComponent from 'screens/common/components/table';
import { cropInfectionApi } from 'api/endpoints/CropInfection';
import { useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import { ConfirmationModal } from 'screens/common/ConfirmationModal';
import FalconCardHeader from 'components/common/FalconCardHeader';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilter } from '@fortawesome/free-solid-svg-icons';
import * as XLSX from 'xlsx';
import CardDropdown from 'components/common/CardDropdown';
import { getTenantFromToken } from 'utils/routeUtils';

export const CropInfectionRulesReport = () => {
  const tenant = getTenantFromToken();
  const tenantId = '03534add672340809e2c70314b1ce20e';
  const navigate = useNavigate();
  const [tableState, setTableState] = useState({
    data: [],
    cursor: 0,
    total: 0,
    loading: false,
    exportKey: '',
    error: null
  });

  const [filters, setFilters] = useState({
    limit: 10,
    search_text: '',
    cursor: 0
  });

  const [modalState, setModalState] = useState({
    isOpen: false,
    mode: 'create',
    selectedRule: null,
    formSchema: null
  });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [showFilter, setShowFilter] = useState(false);
  const [exportdata, setExportData] = useState(null);
  const [exportKey, setExportKey] = useState(null);

  const stableFilters = useCallback(filters, [filters]);

  // Fetch table data
  const fetchTableData = useCallback(async () => {
    setTableState(prev => ({ ...prev, loading: true, error: null }));
    try {
      const response = await cropInfectionApi.getInfectionRuleReport(
        tenantId,
        stableFilters
      );
      setTableState({
        data: response.data?.results?.data || [],
        cursor: response.data?.results?.next_cursor,
        total: response.data?.results?.total || 0,
        loading: false,
        error: null
      });
      setExportData(response?.data?.results?.data);
      setExportKey(response?.data?.results?.export_key);
    } catch (err) {
      setTableState(prev => ({
        ...prev,
        loading: false,
        error: 'Failed to fetch data'
      }));
    }
  }, [stableFilters, tenantId]);

  // Debounced search handler
  const handleSearch = useCallback(
    debounce(searchText => {
      setFilters(prev => ({
        ...prev,
        search_text: searchText,
        cursor: 0 // Reset cursor on new search
      }));
    }, 300),
    []
  );

  const handlePageChange = useCallback(newCursor => {
    setFilters(prev => ({
      ...prev,
      cursor: newCursor * filters.limit
    }));
  }, []);

  const handleFormSubmit = async formData => {
    const { mode, selectedRule } = modalState;
    try {
      if (mode === 'edit' && selectedRule) {
        await cropInfectionApi.updateRule(tenantId, selectedRule.id, formData);
      } else {
        await cropInfectionApi.createRule(tenantId, formData);
      }
      toast.success(
        `Rule ${mode === 'edit' ? 'updated' : 'created'} successfully`
      );
      handleCloseModal();
      fetchTableData();
    } catch (err) {
      toast.error(`Failed to ${mode} rule`);
    }
  };

  const handleOpenModal = async (mode, rowData = null) => {
    setModalState(prev => ({
      ...prev,
      isOpen: true,
      mode,
      selectedRule: rowData
    }));
    try {
      let isEdit = true;
      const formResponse = await cropInfectionApi.getDynamicForm(
        tenantId,
        rowData['id'],
        isEdit
      );
      let formSchema = formResponse.data.results;
      // if (mode === 'edit' && rowData?.id) {
      //   const editResponse = await cropInfectionApi.getEditFormData(
      //     tenantId,
      //     rowData['id']
      //   );

      //   if (editResponse?.data?.results) {
      //     formSchema.fields = formSchema.fields.map(field => {
      //       if (field.table) {
      //         // Handle table field, replace rows with the data from the edit response
      //         const tableData = editResponse.data.results[field.id] || [];

      //         return {
      //           ...field,
      //           table: {
      //             ...field.table,
      //             rows: tableData.length > 0 ? tableData : field.table.rows // Replace with editResponse rows if available
      //           }
      //         };
      //       } else if (field.type === 'toggle_with_dropdown') {
      //         // Handle toggle_with_dropdown dynamically
      //         const toggleValue = editResponse.data.results[field.id];
      //         const dropdownKey = toggleValue; // Use the toggle value as the dropdown key
      //         const dropdownField = field.options?.[dropdownKey];

      //         return {
      //           ...field,
      //           defaultValue: toggleValue, // Set the toggle value
      //           options: {
      //             ...field.options,
      //             [dropdownKey]: {
      //               ...dropdownField,
      //               defaultValue:
      //                 editResponse.data.results[dropdownKey] ||
      //                 dropdownField.defaultValue
      //             }
      //           }
      //         };
      //       } else {
      //         return {
      //           ...field,
      //           defaultValue:
      //             editResponse.data.results[field.id] || field.defaultValue
      //         };
      //       }
      //     });
      //   }
      // } else if (mode === 'create') {
      //   history.push('/cropInfection/rules/create');
      // }

      setModalState(prev => ({ ...prev, formSchema }));
    } catch (err) {
      toast.error('Failed to load form');
      handleCloseModal();
    }
  };

  const handleCloseModal = () => {
    setModalState({
      isOpen: false,
      mode: 'create',
      selectedRule: null,
      formSchema: null
    });
  };

  const handleDelete = async rowData => {
    setSelectedTemplateId(rowData.id);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async id => {
    if (id) {
      try {
        await cropInfectionApi.deleteRule(tenantId, id);
        toast.success('Rule deleted successfully');
        fetchTableData();
      } catch (error) {
        toast.error(
          `Failed to delete rule: ${error.message || 'Unknown error'}`
        );
      }
    }

    // Close the modal after the operation, regardless of success or failure
    setShowDeleteModal(false);
  };

  useEffect(() => {
    fetchTableData();
  }, [fetchTableData]);

  const handleDownload = () => {
    const filename = 'Crop_Infection_Rules.xlsx';
    const excelData = convertToExcelData(exportdata, filename);
    downloadExcel(excelData, filename);
  };

  const convertToExcelData = (data, fileName) => {
    if (!data?.length) return [];

    const now = new Date();
    const timestamp = now
      .toLocaleString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      })
      .replace(',', ' at');

    const readableName = fileName
      .replace('.xlsx', '')
      .replace(/[_-]/g, ' ')
      .replace(/\b\w/g, c => c.toUpperCase());

    const reportTitle = `${readableName} – Generated on ${timestamp} from the FARMSIO Application`;

    const formatHeader = key =>
      key.replace(/[_-]/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

    const flattenValue = val =>
      Array.isArray(val)
        ? val.join('; ')
        : typeof val === 'object' && val !== null
        ? Object.entries(val)
            .map(([k, v]) => `${k}: ${v}`)
            .join('; ')
        : val ?? 'NULL';

    const rawHeaders = Object.keys(data[0]).filter(
      k => k !== 'id' && k !== 'action'
    );
    const formattedHeaders = rawHeaders.map(formatHeader);
    const dataRows = data.map(item =>
      rawHeaders.map(key => flattenValue(item[key]))
    );

    return [[reportTitle], [], formattedHeaders, ...dataRows];
  };

  const downloadExcel = (excelData, filename) => {
    const ws = XLSX.utils.aoa_to_sheet(excelData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Crop Infection Rule Report');
    XLSX.writeFile(wb, filename);
  };

  return (
    <Container fluid>
      <Row className="d-flex justify-content-end mb-2">
        <Col className="text-end">
          <Button
            onClick={() => navigate(`/${tenant}/cropInfection/rules/create`)}
          >
            Create Rule
          </Button>
        </Col>
      </Row>
      <Card>
        {/* <Card.Header>
          <Row>
            <Col>
              <h5>Crop Infection Rules</h5>
            </Col>
            <Col xs="auto">
              <Button
                onClick={() =>
                  navigate(`/${tenant}/cropInfection/rules/create`)
                }
              >
                Create Rule
              </Button>
            </Col>
          </Row>
        </Card.Header> */}
        <FalconCardHeader
          title="Crop Infection Rules"
          titleTag="h5"
          light={true}
          endEl={
            <div className="d-flex justify-content-end my-auto">
              {/* <div
                className="px-2 my-auto theme-control-toggle"
                onClick={() => setShowFilter(!showFilter)}
              >
                <OverlayTrigger
                  key="filter"
                  placement={'bottom'}
                  overlay={<Tooltip id="FilterData">Filter</Tooltip>}
                >
                  <div className="theme-control-toggle-label">
                    <FontAwesomeIcon icon={faFilter} className="fs-0" />
                  </div>
                </OverlayTrigger>
              </div> */}
              <CardDropdown onExport={handleDownload} exportKey={exportKey} />
            </div>
          }
        />
        <Card.Body>
          {tableState.error && (
            <Alert variant="danger">{tableState.error}</Alert>
          )}
          {/* {tableState.loading ? (
            <div className="text-center">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : null} */}
          <TableComponent
            data={tableState.data}
            total={tableState.total}
            cursor={filters.cursor}
            limit={filters.limit}
            setPageLimit={setFilters}
            onPageChange={handlePageChange}
            onSearch={handleSearch}
            onEdit={(_, rowData) => handleOpenModal('edit', rowData)}
            onDelete={handleDelete}
            setExportData={setExportData}
            showFilter={showFilter}
          />
        </Card.Body>
      </Card>

      <Modal
        show={modalState.isOpen}
        onHide={handleCloseModal}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {modalState.mode === 'edit'
              ? modalState?.formSchema?.form?.title
              : 'Create Rule'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {!isEmpty(modalState.formSchema?.fields) && (
            <DynamicForm
              data={modalState.formSchema}
              modalType="lg"
              onSubmit={handleFormSubmit}
            />
          )}
        </Modal.Body>
      </Modal>
      <ConfirmationModal
        showModal={showDeleteModal}
        handleClose={() => setShowDeleteModal(false)}
        handleConfirm={handleDeleteConfirm}
        templateId={selectedTemplateId}
        actionType="delete"
        title="Crop Infection Rule"
      />
    </Container>
  );
};
